/**
 * V113-7-0 — client for the v11.3 monitoring surface (Track 7 data layer). Kept out of lib/api.ts so
 * parallel tracks don't collide on one file. Every call is workspace-scoped (`/api/w/:slug/...`); the
 * server resolves the workspace from the session and 404s on any id outside it. Role gates are the
 * server's (see monitoring-capabilities.ts for the client mirror used to disable actions).
 *
 * Routes (packages/cloudflare/src/routes):
 *   system-monitoring.ts           GET  systems/:id/monitoring · GET …/monitoring/timeline
 *                                  POST …/monitoring/candidates/:candidateId/accept · POST systems/:id/class
 *   configuration-items.ts         GET / · GET /:id · PATCH /:id · POST /:id/link-system · /:id/merge
 *                                  · /:id/retire · /:id/methods/:methodId/verify
 *   monitoring-feeds.ts            GET monitoring/pack-validity · GET/POST monitoring/feeds
 *                                  · DELETE monitoring/feeds/:id · POST monitoring/feeds/:id/rotate-link
 *   monitoring-plan.ts             GET systems/:id/monitoring-plan(/packages(/:packageId)) · POST …/packages
 *   system-anomaly-thresholds.ts   GET / PUT systems/:id/anomaly-thresholds
 *   insurability-pack.ts           POST insurability-pack/packages (re-sign with carried exceptions, D-113-29)
 *   PUBLIC (no auth)               GET /verify/monitoring-status/:link_id · /verify/monitoring-status/snapshot/:snapshot_id
 */
import type {
  AcceptOutcome,
  CandidateSetupResult,
  CheckObservation,
  ConfigChangeCause,
  CarriedCounts,
  CarriedGroup,
  MonitoringInProgressAct,
  NextMonitoringAction,
  PackHistoryEntry,
  PackValidityStatus,
  PackValidityTransition,
  PlanFreshness,
  SystemPackState,
  AnomalyThresholdOverride,
  AnomalyThresholdOverrideInput,
  AnomalyThresholdView,
  CdsRecipientType,
  CiEnvironment,
  ClassEvidence,
  ComponentSystemLink,
  ConfigurationItemDetail,
  ConfigurationItemView,
  DetectorCandidate,
  MergeOutcome,
  MonitoringBinding,
  MonitoringFeed,
  MonitoringMethod,
  MonitoringPlanDocument,
  MonitoringPlanPackage,
  MonitoringPlanSummary,
  PackValidity,
  PrescriptionControl,
  SiblingOffer,
  SystemClass,
  SystemComponent,
  SystemCoverage,
  SystemMonitoringContext,
  TimelineEntry,
  TimelinePage,
} from '@aigovops/core';
import { ApiError, req } from './api';

export type {
  AnomalyThresholdView, CdsRecipientType, CiEnvironment, ConfigurationItemDetail, ConfigurationItemView, DetectorCandidate,
  MonitoringFeed, MonitoringMethod, MonitoringPlanDocument, MonitoringPlanSummary, PackValidity, SystemClass, SystemCoverage,
  TimelineEntry, TimelinePage, CandidateSetupResult, CheckObservation, ConfigChangeCause, CarriedCounts, CarriedGroup, NextMonitoringAction, PackHistoryEntry, PackValidityTransition, PlanFreshness, SystemPackState,
};

const enc = encodeURIComponent;
const ws = (slug: string) => `/api/w/${enc(slug)}`;
const send = <T>(path: string, method: string, body?: unknown) =>
  req<T>(path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

// ---------------------------------------------------------------------------
// System monitoring (V113-1-7, V113-3-4)
// ---------------------------------------------------------------------------

export type MonitoringAcceptance = SystemMonitoringContext['acceptances'][number];

/** GET /systems/:id/monitoring — any member. */
export interface SystemMonitoringView {
  system: { system_id: string; name: string };
  class: {
    /** Set only when a member declared it. */
    declared: string | null;
    current: string | null;
    source: 'declared' | 'inferred' | string | null;
    inferred: string | null;
    confidence: number | null;
    evidence: ClassEvidence[];
    rule_set_version: number | null;
    inferred_at: string | null;
    /** V113-7 (W-21, 838): who declared / confirmed the class, when, why, and when it is due for re-review. Null for an inferred class. */
    declared_by: string | null;
    declared_by_name: string | null;
    declared_at: string | null;
    reason: string | null;
    /** YYYY-MM-DD. */
    review_by: string | null;
  };
  /** V113-7: each prescribed control with its title. */
  prescription: Array<PrescriptionControl & { title: string | null }>;
  /**
   * V113-7: `setup` = the real pipeline step for a candidate that needs one (`kind`, `snippet` (GitHub step YAML),
   * `cli_snippet`, `docs_href`, `format`, `tool`, `file`, `produce`); null otherwise, with `setup_unavailable_reason`
   * when a pipeline candidate has no submit path. Never `gac step` (it does not exist).
   */
  candidates: Array<DetectorCandidate & CandidateSetupResult>;
  sibling_offers: Array<SiblingOffer & CandidateSetupResult>;
  unmatched: Array<{ control_id: string; detectors: string[]; relevant_ci_kinds: string[] }>;
  components: SystemComponent[];
  app_component_id: string | null;
  /** V113-7: with the control title and `latest` (expected vs actual of the last check; null before the first run). */
  bindings: Array<MonitoringBinding & { control_title: string | null; latest: CheckObservation | null }>;
  acceptances: MonitoringAcceptance[];
  /**
   * V113-7: `latest` = the newest check of any binding naming the method (null before its first run). B-3: a method
   * fed by pipeline results (`detector_kind: 'ci_parser'`) carries the same `setup` as its candidate (how to re-run it),
   * `last_run_ref` (the CI run url of its last check, when sent) and `last_run_at`; other methods have nulls there.
   */
  methods: Array<MonitoringMethod & CandidateSetupResult & { latest: CheckObservation | null; last_run_ref: string | null; last_run_at: string | null }>;
  coverage: SystemCoverage;
  /** V113-7: the same per-system facts the Monitoring table shows (passing is the headline). */
  facts: SystemMonitoringFactsView;
  /** V113-7 (X-10): is the latest SIGNED monitoring plan out of date. */
  plan_freshness: PlanFreshness;
}

/** V113-7 — per-system facts (GET /systems/:id/monitoring `facts`). */
export interface SystemMonitoringFactsView {
  passing: number;
  of: number;
  /** Controls with a working method (passing or not). */
  covered: number;
  pack_state: SystemPackState | null;
  carried: CarriedCounts | null;
  attested_pass: number | null;
  attested_of: number | null;
  open_issues: number;
  open_by_kind: Record<string, number>;
  owner_name: string | null;
  /** D-113-31 signed layer: New until one of its methods reports. */
  new_since_pack: boolean;
  new_since: string | null;
  /** D-113-31 internal layer (never signed). */
  in_progress: { by_name: string | null; since: string; act: MonitoringInProgressAct; link: string | null } | null;
  sla_due: string | null;
  next_action: NextMonitoringAction | null;
}

/** Includes `bindings` (the same rows the v5 `{ bindings }` read returned; that route was removed in V113-7-0). */
export const getSystemMonitoring = (slug: string, systemId: string) =>
  req<SystemMonitoringView>(`${ws(slug)}/systems/${enc(systemId)}/monitoring`);

export interface TimelineQuery {
  /** ISO date or date-time; a date-only `from` is the start of that UTC day. Default: `to` − 30 days. */
  from?: string;
  /** ISO date or date-time; a date-only `to` is the end of that UTC day. Default: now. */
  to?: string;
  /** 1..200 (default 50). */
  limit?: number;
  /** The `next_cursor` of the previous page, exactly as issued. */
  cursor?: string | null;
  control_id?: string;
  only?: 'all' | 'changes';
}

export const TIMELINE_MAX_WINDOW_DAYS = 90;
export const TIMELINE_MAX_LIMIT = 200;

/** Query string for the timeline; only keys the strict server schema accepts (an unknown key is a 422). */
export function timelineSearch(q: TimelineQuery = {}): string {
  const p = new URLSearchParams();
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  if (q.limit !== undefined) p.set('limit', String(Math.trunc(q.limit)));
  if (q.cursor) p.set('cursor', q.cursor);
  if (q.control_id) p.set('control_id', q.control_id);
  if (q.only) p.set('only', q.only);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** GET /systems/:id/monitoring/timeline — any member; keyset-paginated (newest first). */
export const getMonitoringTimeline = (slug: string, systemId: string, q: TimelineQuery = {}) =>
  req<TimelinePage & { system: { system_id: string; name: string } }>(
    `${ws(slug)}/systems/${enc(systemId)}/monitoring/timeline${timelineSearch(q)}`);

/** POST …/monitoring/candidates/:candidateId/accept — owner / admin / editor. 404 when the recompute no longer proposes it. */
export const acceptCandidate = (slug: string, systemId: string, candidateId: string) =>
  send<{ accepted: Pick<AcceptOutcome, 'candidate' | 'method' | 'binding'> & { acceptance: MonitoringAcceptance } }>(
    `${ws(slug)}/systems/${enc(systemId)}/monitoring/candidates/${enc(candidateId)}/accept`, 'POST', {});

/**
 * POST /systems/:id/class — owner / admin / editor. Declares a class, or confirms the inferred one by sending it.
 * V113-7 (W-21): optional `reason` (audited) and `review_by` (YYYY-MM-DD, not in the past; default today + 12 months).
 */
export const declareSystemClass = (slug: string, systemId: string, systemClass: SystemClass, opts: { reason?: string; review_by?: string } = {}) =>
  send<{ class: { declared: SystemClass; previous: string | null; families_added: string[]; drift: 'opened' | 'reopened' | 'closed' | 'unchanged';
    declared_by: string; declared_at: string; reason: string | null; review_by: string } }>(
    `${ws(slug)}/systems/${enc(systemId)}/class`, 'POST', {
      system_class: systemClass,
      ...(opts.reason?.trim() ? { reason: opts.reason.trim() } : {}),
      ...(opts.review_by ? { review_by: opts.review_by } : {}),
    });

// ---------------------------------------------------------------------------
// Configuration items (V113-0-7, D-113-27, D-113-28)
// ---------------------------------------------------------------------------

export interface ConfigurationItemListQuery {
  system_id?: string;
  environment?: CiEnvironment;
  include_gone?: boolean;
  include_retired?: boolean;
}

export function configurationItemSearch(q: ConfigurationItemListQuery = {}): string {
  const p = new URLSearchParams();
  if (q.system_id) p.set('system_id', q.system_id);
  if (q.environment) p.set('environment', q.environment);
  if (q.include_gone) p.set('include_gone', '1');
  if (q.include_retired) p.set('include_retired', '1');
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const listConfigurationItems = (slug: string, q: ConfigurationItemListQuery = {}) =>
  req<{ configuration_items: ConfigurationItemView[] }>(`${ws(slug)}/configuration-items${configurationItemSearch(q)}`);

/** V113-7 (Y-1): one binding of a configuration item, with its last check's expected vs actual. */
export interface ConfigurationItemBinding {
  binding_id: string;
  system_id: string;
  control_id: string;
  control_title: string | null;
  method_id: string | null;
  binding_status: string;
  last_check_id: string | null;
  latest: CheckObservation | null;
}

/** GET /configuration-items/:id — methods carry `latest`; `bindings` lists this item's bindings (values only where recorded). */
export type ConfigurationItemRead = Omit<ConfigurationItemDetail, 'methods'> & {
  methods: Array<MonitoringMethod & { latest: CheckObservation | null }>;
  bindings: ConfigurationItemBinding[];
};

export const getConfigurationItem = (slug: string, id: string) =>
  req<{ configuration_item: ConfigurationItemRead }>(`${ws(slug)}/configuration-items/${enc(id)}`);

/** Name / tag / owner / tags: any member. A change to `environment`: owner / admin / editor (403 otherwise). */
export interface ConfigurationItemPatch {
  ci_name?: string;
  ci_tag?: string | null;
  owner_user_id?: string | null;
  environment?: CiEnvironment | null;
  tags?: string[];
}

export function updateConfigurationItem(slug: string, id: string, patch: ConfigurationItemPatch) {
  // The server refuses an empty body (422 "Nothing to change"); drop undefined keys so the strict schema sees only intent.
  const body = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined));
  return send<{ configuration_item: ConfigurationItemView }>(`${ws(slug)}/configuration-items/${enc(id)}`, 'PATCH', body);
}

export type CiScope = 'in_scope' | 'out_of_scope';
export interface CiScopeDecision { asset_id: string; ci_scope: CiScope; ci_scope_decided_at: string; ci_scope_decided_by: string; /** 836 */ ci_scope_reason: string | null }

/** Manual attribution (or re-review of an existing link). Owner / admin / editor. */
export const linkConfigurationItemToSystem = (slug: string, id: string, systemId: string) =>
  send<{ link: ComponentSystemLink }>(`${ws(slug)}/configuration-items/${enc(id)}/link-system`, 'POST', { system_id: systemId });

/**
 * In / out of scope for an already-linked system (404 when not linked). `in_scope`: owner / admin / editor.
 * `out_of_scope` (836, D-113-27/28): a non-empty `reason` (400 `reason_required`, every role); a passing item →
 * owner / admin / editor; a FAILING item → owner / admin (403 `failing_requires_owner_admin`) and makes pack validity stale.
 */
export function setConfigurationItemScope(slug: string, id: string, systemId: string, scope: 'out_of_scope', reason: string): Promise<{ scope: CiScopeDecision }>;
export function setConfigurationItemScope(slug: string, id: string, systemId: string, scope: 'in_scope'): Promise<{ scope: CiScopeDecision }>;
export function setConfigurationItemScope(slug: string, id: string, systemId: string, scope: CiScope, reason?: string) {
  return send<{ scope: CiScopeDecision }>(`${ws(slug)}/configuration-items/${enc(id)}/link-system`, 'POST',
    scope === 'out_of_scope' ? { system_id: systemId, scope, reason: reason ?? '' } : { system_id: systemId, scope });
}

/** Merge `:id` (the loser) into `intoComponentId` (the survivor). Owner / admin. */
export const mergeConfigurationItem = (slug: string, id: string, intoComponentId: string) =>
  send<{ merge: MergeOutcome; configuration_item: ConfigurationItemDetail | null }>(
    `${ws(slug)}/configuration-items/${enc(id)}/merge`, 'POST', { into_component_id: intoComponentId });

/** Asset-writer bar (owner / admin or the lead of an asset-scoping program). 409 while in an assessed category in fieldwork. */
export const retireConfigurationItem = (slug: string, id: string) =>
  send<{ configuration_item: ConfigurationItemView }>(`${ws(slug)}/configuration-items/${enc(id)}/retire`, 'POST', {});

/** Member attestation of a method, time-boxed. Owner / admin / editor; 409 on a retired method. */
export const verifyConfigurationItemMethod = (slug: string, id: string, methodId: string) =>
  send<{ method: MonitoringMethod }>(`${ws(slug)}/configuration-items/${enc(id)}/methods/${enc(methodId)}/verify`, 'POST', {});

// ---------------------------------------------------------------------------
// Pack validity + shared feeds (V113-5-1..5-3, D-113-24)
// ---------------------------------------------------------------------------

/**
 * Any member. `validity` is null before any pack is signed. V113-7: `validity.status` may be
 * `current_with_exceptions` (with `carried`), `validity.transitions` is this pack's line, and `history` lists
 * every signed pack newest first (version, attested date, digest, replaced date, transitions).
 */
export const getPackValidity = (slug: string) =>
  req<{ validity: PackValidity | null; history: PackHistoryEntry[]; can_manage_feeds: boolean }>(`${ws(slug)}/monitoring/pack-validity`);

/** D-113-29: the body of a re-sign over open problems. */
export interface SignPackInput {
  /** Required (non-empty) when the pack would carry failing controls or overdue checks. */
  reason?: string;
  /** Must name every carried group present. */
  acknowledge?: CarriedGroup[];
}

/** The 400 body when a re-sign would carry problems not yet acknowledged (D-113-29). */
export interface ExceptionsUnacknowledged {
  error: string;
  code: 'exceptions_unacknowledged';
  carried: CarriedCounts;
  required: CarriedGroup[];
}

/**
 * POST insurability-pack/packages — owner / admin. With carried problems: 400 `exceptions_unacknowledged`
 * (see {@link ExceptionsUnacknowledged}) until `reason` + `acknowledge` cover them. Systems with no class stay
 * out of the pack. 503 `not_configured` without the signer.
 */
export const signPackWithExceptions = (slug: string, input: SignPackInput = {}) =>
  send<{ package: { package_id: string; version: number; content_sha256: string; signed_at: string } }>(`${ws(slug)}/insurability-pack/packages`, 'POST', input);

export type MonitoringFeedRow = MonitoringFeed & { verify_url: string | null };
export interface SystemAlias { alias: string; system_id: string; name: string | null }

/** Owner / admin (403 otherwise). Token hints only; never a token. */
export const listMonitoringFeeds = (slug: string) =>
  req<{ feeds: MonitoringFeedRow[]; system_aliases: SystemAlias[] }>(`${ws(slug)}/monitoring/feeds`);

export interface IssueFeedInput {
  recipient_name: string;
  recipient_contact_email: string;
  recipient_type: CdsRecipientType;
  /** ISO date-time. */
  expires_at: string;
}

/**
 * The issue response. `token` is the FULL bearer token, returned exactly once: keep this object (or the
 * token string itself) in memory and copy from it — never re-read it from rendered text.
 */
export interface IssuedFeed {
  feed: MonitoringFeedRow;
  token: string;
  token_id: string;
  token_hint: string;
  read_url: string;
  note: string;
}

/** Owner / admin. 503 `not_configured` without the signer; 422 `no_pack` before a pack is signed. */
export const issueMonitoringFeed = (slug: string, input: IssueFeedInput) =>
  send<IssuedFeed>(`${ws(slug)}/monitoring/feeds`, 'POST', input);

export const revokeMonitoringFeed = (slug: string, grantId: string) =>
  send<{ revoked: true }>(`${ws(slug)}/monitoring/feeds/${enc(grantId)}`, 'DELETE');

export const rotateMonitoringFeedLink = (slug: string, grantId: string) =>
  send<{ link_id: string; verify_url: string }>(`${ws(slug)}/monitoring/feeds/${enc(grantId)}/rotate-link`, 'POST', {});

/**
 * V113-7: replace the feed's bearer token and KEEP its verify link. Owner / admin. The FULL new token is returned
 * once (keep the object; copy from it); the old token stops working at once. 404 for a revoked or foreign feed.
 */
export interface RotatedFeedToken { token: string; token_id: string; token_hint: string; verify_url: string | null; read_url: string; note: string }
export const rotateMonitoringFeedToken = (slug: string, grantId: string) =>
  send<RotatedFeedToken>(`${ws(slug)}/monitoring/feeds/${enc(grantId)}/rotate-token`, 'POST', {});

/** Display form of a one-time token (`abcd…wxyz`). For display ONLY — copy uses the original value. */
export function maskToken(token: string): string {
  return token.length <= 12 ? '•'.repeat(token.length) : `${token.slice(0, 6)}…${token.slice(-4)}`;
}

// ---------------------------------------------------------------------------
// Post-market monitoring plan (V113-6-4)
// ---------------------------------------------------------------------------

/** `freshness`: is the latest SIGNED plan out of date (V113-7). `class_confirmed`: false while the class is inferred (Sign is 409 then). */
export const getMonitoringPlan = (slug: string, systemId: string) =>
  req<{ document: MonitoringPlanDocument; can_sign: boolean; class_confirmed: boolean; freshness: PlanFreshness }>(`${ws(slug)}/systems/${enc(systemId)}/monitoring-plan`);

export const listMonitoringPlanPackages = (slug: string, systemId: string) =>
  req<{ packages: MonitoringPlanSummary[]; can_sign: boolean; freshness: PlanFreshness }>(`${ws(slug)}/systems/${enc(systemId)}/monitoring-plan/packages`);

export interface PlanVerification { valid: boolean; content_sha256_matches: boolean | null; signature_valid: boolean | null; reason?: 'signer_unavailable' }

export const getMonitoringPlanPackage = (slug: string, systemId: string, packageId: string) =>
  req<{ package: MonitoringPlanPackage; verification: PlanVerification }>(
    `${ws(slug)}/systems/${enc(systemId)}/monitoring-plan/packages/${enc(packageId)}`);

/** Owner / admin. 503 without the signer; 422 with no class / prescription; 409 `class_not_confirmed` while inferred. A new immutable version each time. */
export const signMonitoringPlan = (slug: string, systemId: string) =>
  send<{ package: MonitoringPlanPackage }>(`${ws(slug)}/systems/${enc(systemId)}/monitoring-plan/packages`, 'POST', {});

// ---------------------------------------------------------------------------
// PUBLIC verify (no auth): the signed monitoring status, live by link or one immutable snapshot (V113-7)
// ---------------------------------------------------------------------------

/** One system row of the public body: by position only (no alias, no name); counts only. */
export interface PublicStatusSystem {
  covered: number;
  applicable: number;
  in_pack: boolean;
  /** Controls with a working method; null on a /1 snapshot. */
  monitored: number | null;
  /** D-113-30: open monitoring issues (count only). */
  open_issues: number;
  /** D-113-31 signed layer. */
  new_since_pack: boolean;
  new_since: string | null;
  attested_pass: number | null;
  attested_of: number | null;
  pack_state: SystemPackState | null;
  carried: CarriedCounts | null;
}

/** The signed-fields-only body of /verify/monitoring-status/:link_id and …/snapshot/:snapshot_id (same shape). */
export interface PublicMonitoringStatus {
  kind: 'monitoring_status';
  pack: { version: number; attested_at: string; content_sha256: string; signed_with_exceptions: boolean };
  validity: {
    status: PackValidityStatus;
    rules_pending: boolean;
    reasons: Array<{ code: string; effect: 'stale' | 'invalidated'; clears: 'automatic' | 'resign'; count: number; label: string; since: string | null }>;
    next_at_risk_at: string | null;
    carried: CarriedCounts | null;
  };
  history: Array<{ version: number; attested_at: string; content_sha256: string; replaced_at: string | null; carried_at_signing: CarriedCounts | null; transitions: PackValidityTransition[] }>;
  coverage: { score: number | null; systems: PublicStatusSystem[]; systems_not_in_pack: number; systems_not_covered_label: string };
  /** 836: out-of-scope decisions — reason, when, whether its check fails (no names). */
  out_of_scope: Array<{ reason: string | null; decided_at: string | null; failing: boolean }>;
  last_anomaly_at: string | null;
  last_config_change_at: string | null;
  last_verified_at: string;
  content_sha256: string;
  snapshot_signed_at: string;
  /** This snapshot's own immutable url ("Cite this snapshot"). */
  snapshot_url: string;
  signature_valid: boolean;
  content_matches_digest: boolean;
  /** True when it verified with a key the workspace has since retired (snapshots never expire on rotation). */
  signing_key_retired: boolean;
}

/** 410 body of a rotated link. */
export interface PublicLinkReplaced { status: 'link_replaced'; replaced_at: string; detail: string }

const pubJson = { headers: { Accept: 'application/json' } };
/** Live status by feed link. 404 unknown / revoked / expired; 410 {@link PublicLinkReplaced} on a rotated link. */
export const getPublicMonitoringStatus = (linkId: string) =>
  req<PublicMonitoringStatus>(`/verify/monitoring-status/${enc(linkId)}?format=json`, pubJson);
/** One immutable snapshot. 404 only for an unknown id; never 410 (link or key rotation never breaks it). */
export const getPublicMonitoringSnapshot = (snapshotId: string) =>
  req<PublicMonitoringStatus>(`/verify/monitoring-status/snapshot/${enc(snapshotId)}?format=json`, pubJson);

// ---------------------------------------------------------------------------
// Anomaly thresholds (V113-2-5)
// ---------------------------------------------------------------------------

export const getAnomalyThresholds = (slug: string, systemId: string) =>
  req<AnomalyThresholdView>(`${ws(slug)}/systems/${enc(systemId)}/anomaly-thresholds`);

/** Owner / admin. Replaces the system's overrides (empty = clear). 409 `no_rules` before the rule set is approved. */
export const putAnomalyThresholds = (slug: string, systemId: string, overrides: AnomalyThresholdOverrideInput[]) =>
  send<{ overrides: AnomalyThresholdOverride[]; thresholds: AnomalyThresholdView }>(
    `${ws(slug)}/systems/${enc(systemId)}/anomaly-thresholds`, 'PUT', { overrides });

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type MonitoringErrorKind =
  | 'not_found' | 'forbidden' | 'validation' | 'conflict' | 'not_configured' | 'no_pack' | 'unplaced' | 'no_rules'
  // V113-7: 400 reason_required (out of scope), 400 exceptions_unacknowledged (re-sign), 409 class_not_confirmed (plan sign).
  | 'reason_required' | 'exceptions_unacknowledged' | 'class_not_confirmed' | 'unknown';

export interface MonitoringError {
  kind: MonitoringErrorKind;
  status: number;
  /** Server words, safe to show (never an id the caller did not send). */
  message: string;
  code: string | null;
  /** Roles that may do this, when the server said (403). */
  who_can: string[] | null;
  /** 422 field errors from the strict schemas. */
  fields: Array<{ path: string; message: string }>;
  /** `exceptions_unacknowledged`: what the pack would carry and the groups to acknowledge. */
  carried?: CarriedCounts;
  required?: CarriedGroup[];
}

const ROLE_LIST = /Requires role: ([a-z_, ]+?)\.(?:\s|$)/;

/** Normalize a thrown error from any call above into one shape the pages can branch on. */
export function monitoringErrorOf(err: unknown): MonitoringError {
  if (!(err instanceof ApiError)) {
    return { kind: 'unknown', status: 0, message: err instanceof Error ? err.message : 'Something went wrong.', code: null, who_can: null, fields: [] };
  }
  const body = (err.body && typeof err.body === 'object' ? err.body : {}) as {
    error?: string; detail?: string; code?: string; who_can?: unknown; errors?: unknown; carried?: CarriedCounts; required?: CarriedGroup[];
  };
  const code = typeof body.code === 'string' ? body.code : null;
  const fields = Array.isArray(body.errors)
    ? (body.errors as Array<{ path?: unknown; message?: unknown }>).map((e) => ({ path: String(e.path ?? ''), message: String(e.message ?? '') }))
    : [];
  let who_can: string[] | null = Array.isArray(body.who_can) ? (body.who_can as unknown[]).map(String) : null;
  if (!who_can && err.status === 403 && typeof body.detail === 'string') {
    const m = ROLE_LIST.exec(body.detail);
    if (m) who_can = m[1]!.split(',').map((s) => s.trim()).filter(Boolean);
  }
  const base = { status: err.status, message: err.message, code, who_can, fields };
  switch (err.status) {
    // 404 covers both "gone" and "not yours" (anti-enumeration): the page says one thing for both.
    case 400:
      if (code === 'exceptions_unacknowledged') return { ...base, kind: 'exceptions_unacknowledged', carried: body.carried, required: body.required ?? [] };
      return { ...base, kind: code === 'reason_required' ? 'reason_required' : 'validation' };
    case 404: return { ...base, kind: 'not_found' };
    case 403: return { ...base, kind: 'forbidden' };
    case 409: return { ...base, kind: code === 'unplaced' ? 'unplaced' : code === 'no_rules' ? 'no_rules' : code === 'class_not_confirmed' ? 'class_not_confirmed' : 'conflict' };
    case 422: return { ...base, kind: code === 'no_pack' ? 'no_pack' : 'validation' };
    case 503: return { ...base, kind: code === 'not_configured' ? 'not_configured' : 'unknown' };
    default: return { ...base, kind: 'unknown' };
  }
}
